import { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Check, Eye, Pencil, Search } from 'lucide-react';
import { settingsApi } from '../../services/settingsApi';
import type { PageInfo, PermissionMatrixEntry, MatrixUser } from '../../types/settings';

type PermKey = `${string}-${number}`;

interface PermState {
    canView: boolean;
    canEdit: boolean;
}

export function PermissionMatrix() {
    const [pages, setPages] = useState<PageInfo[]>([]);
    const [users, setUsers] = useState<MatrixUser[]>([]);
    const [matrix, setMatrix] = useState<Map<PermKey, PermState>>(new Map());
    const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadMatrix();
    }, []);

    async function loadMatrix() {
        setLoading(true);
        setError(null);
        try {
            const data = await settingsApi.getPermissionMatrix();
            setPages(data.pages);
            setUsers(data.users);

            const m = new Map<PermKey, PermState>();
            for (const u of data.users) {
                for (const page of data.pages) {
                    m.set(`${u.email}-${page.Id}`, { canView: false, canEdit: false });
                }
            }
            for (const p of data.permissions) {
                m.set(`${p.Email}-${p.PageId}`, { canView: p.CanView, canEdit: p.CanEdit });
            }
            setMatrix(m);

            // Auto-select first page
            if (data.pages.length > 0 && !selectedPageId) {
                setSelectedPageId(data.pages[0].Id);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load permission matrix');
        } finally {
            setLoading(false);
        }
    }

    const togglePerm = useCallback((email: string, pageId: number, field: 'canView' | 'canEdit') => {
        setSaved(false);
        setMatrix(prev => {
            const next = new Map(prev);
            const key: PermKey = `${email}-${pageId}`;
            const current = next.get(key) || { canView: false, canEdit: false };

            if (field === 'canView') {
                const newView = !current.canView;
                next.set(key, {
                    canView: newView,
                    canEdit: newView ? current.canEdit : false,
                });
            } else {
                const newEdit = !current.canEdit;
                next.set(key, {
                    canView: newEdit ? true : current.canView,
                    canEdit: newEdit,
                });
            }
            return next;
        });
    }, []);

    async function handleSave() {
        if (!selectedPageId) return;
        setSaving(true);
        setError(null);
        try {
            const permissions: PermissionMatrixEntry[] = [];
            for (const u of users) {
                const key: PermKey = `${u.email}-${selectedPageId}`;
                const perm = matrix.get(key);
                if (perm) {
                    permissions.push({
                        email: u.email,
                        pageId: selectedPageId,
                        canView: perm.canView,
                        canEdit: perm.canEdit,
                    });
                }
            }
            await settingsApi.updatePermissionMatrix(permissions);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save permissions');
        } finally {
            setSaving(false);
        }
    }

    const selectedPage = pages.find(p => p.Id === selectedPageId);

    const filteredUsers = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return users;
        return users.filter(u =>
            u.email.toLowerCase().includes(q) ||
            u.firstName.toLowerCase().includes(q) ||
            u.lastName.toLowerCase().includes(q) ||
            u.displayName.toLowerCase().includes(q)
        );
    }, [users, searchTerm]);

    function formatName(u: MatrixUser): string {
        const full = `${u.firstName} ${u.lastName}`.trim();
        return full || u.displayName || '—';
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold">Permission Matrix</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        <Eye className="inline h-3 w-3 mr-0.5" /> View &nbsp;
                        <Pencil className="inline h-3 w-3 mr-0.5" /> Edit (includes commenting).
                        Admin always has full access to all pages.
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving || !selectedPageId}
                    className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition-colors ${
                        saved ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'
                    } disabled:opacity-50`}
                >
                    {saved ? <><Check className="h-4 w-4" /> Saved</> : <><Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}</>}
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            {/* Page Selector */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Page</label>
                <div className="flex gap-2 flex-wrap">
                    {pages.map(page => (
                        <button
                            key={page.Id}
                            onClick={() => { setSelectedPageId(page.Id); setSaved(false); }}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
                                selectedPageId === page.Id
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {page.PageName}
                        </button>
                    ))}
                </div>
                {selectedPage && (
                    <p className="text-xs text-gray-400 mt-1">{selectedPage.PageUrl}</p>
                )}
            </div>

            {/* Search */}
            {selectedPageId && users.length > 0 && (
                <div className="mb-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                </div>
            )}

            {/* User Permissions for Selected Page */}
            {selectedPageId && (
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                Email
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500 w-28">
                                <Eye className="inline h-3 w-3 mr-1" />View
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500 w-28">
                                <Pencil className="inline h-3 w-3 mr-1" />Edit
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredUsers.map(u => {
                            const key: PermKey = `${u.email}-${selectedPageId}`;
                            const perm = matrix.get(key) || { canView: false, canEdit: false };
                            return (
                                <tr key={u.email} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        {formatName(u)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {u.email}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => togglePerm(u.email, selectedPageId, 'canView')}
                                            className={`h-6 w-6 rounded border transition-colors ${
                                                perm.canView
                                                    ? 'border-blue-500 bg-blue-500 text-white'
                                                    : 'border-gray-300 bg-white hover:border-gray-400'
                                            }`}
                                        >
                                            {perm.canView && <Check className="h-4 w-4 mx-auto" />}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => togglePerm(u.email, selectedPageId, 'canEdit')}
                                            className={`h-6 w-6 rounded border transition-colors ${
                                                perm.canEdit
                                                    ? 'border-green-500 bg-green-500 text-white'
                                                    : 'border-gray-300 bg-white hover:border-gray-400'
                                            }`}
                                        >
                                            {perm.canEdit && <Check className="h-4 w-4 mx-auto" />}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredUsers.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                                    {users.length === 0
                                        ? 'No users found. Add users in the User Management tab first.'
                                        : 'No users match your search.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {pages.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">No active pages found.</p>
            )}
        </div>
    );
}
